/**
 * SearchResults Component
 *
 * Email search results page with support for three search modes:
 * - Keyword: PostgreSQL full-text search (fast, exact matching)
 * - Semantic: pgvector similarity search (meaning-based)
 * - Hybrid: RRF fusion combining both (best of both worlds)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  searchApi,
  SearchResult,
  SearchMode,
  accountApi,
  EmailAccount,
} from '@/services/emailApi';
import { formatEmailDate } from '@/utils/dateFormat';

// Match type styling
const MATCH_TYPE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  keyword: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Keyword' },
  semantic: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Semantic' },
  both: { bg: 'bg-green-100', text: 'text-green-700', label: 'Both' },
};

const SearchResults: React.FC = () => {
  const { t } = useTranslation('email');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // State
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [mode, setMode] = useState<SearchMode>((searchParams.get('mode') as SearchMode) || 'hybrid');
  const [accountId, setAccountId] = useState<string | undefined>(searchParams.get('account_id') || undefined);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);

  // Load accounts for filter dropdown
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const data = await accountApi.list();
        setAccounts(data);
      } catch (err) {
        console.error('Failed to load accounts:', err);
      }
    };
    loadAccounts();
  }, []);

  // Perform search
  const performSearch = useCallback(async (searchQuery: string, searchMode: SearchMode, searchAccountId?: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await searchApi.search({
        query: searchQuery,
        mode: searchMode,
        accountId: searchAccountId,
        limit: 50,
      });

      setResults(response.results);
      setTotalCount(response.total_count);
    } catch (err) {
      console.error('Search failed:', err);
      setError(t('search.search_failed'));
      setResults([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search when query params change
  useEffect(() => {
    const q = searchParams.get('q') || '';
    const m = (searchParams.get('mode') as SearchMode) || 'hybrid';
    const a = searchParams.get('account_id') || undefined;

    setQuery(q);
    setMode(m);
    setAccountId(a);

    if (q) {
      performSearch(q, m, a);
    }
  }, [searchParams, performSearch]);

  // Handle search form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (mode !== 'hybrid') params.set('mode', mode);
    if (accountId) params.set('account_id', accountId);
    setSearchParams(params);
  };

  // Handle mode change
  const handleModeChange = (newMode: SearchMode) => {
    setMode(newMode);
    const params = new URLSearchParams(searchParams);
    if (newMode !== 'hybrid') {
      params.set('mode', newMode);
    } else {
      params.delete('mode');
    }
    setSearchParams(params);
  };

  // Handle account filter change
  const handleAccountChange = (newAccountId: string) => {
    const params = new URLSearchParams(searchParams);
    if (newAccountId) {
      params.set('account_id', newAccountId);
    } else {
      params.delete('account_id');
    }
    setAccountId(newAccountId || undefined);
    setSearchParams(params);
  };

  // Navigate to thread
  const handleResultClick = (result: SearchResult) => {
    navigate(`/email/thread/${result.thread_id}`);
  };

  // Render highlighted text (for keyword search)
  const renderHighlight = (text: string) => {
    if (!text) return null;
    // Replace <mark> tags with styled spans
    const parts = text.split(/(<mark>.*?<\/mark>)/g);
    return parts.map((part, i) => {
      if (part.startsWith('<mark>') && part.endsWith('</mark>')) {
        const content = part.slice(6, -7);
        return (
          <mark key={i} className="bg-yellow-200 text-yellow-900 px-0.5 rounded">
            {content}
          </mark>
        );
      }
      return part;
    });
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header with search controls */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => navigate('/email')}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-gray-900">{t('search.search_results')}</h1>
        </div>

        {/* Search form */}
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search.search_emails')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Search mode selector */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {(['keyword', 'semantic', 'hybrid'] as SearchMode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => handleModeChange(m)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                  mode === m
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title={
                  m === 'keyword' ? t('search.keyword_description') :
                  m === 'semantic' ? t('search.semantic_description') :
                  t('search.hybrid_description')
                }
              >
                {m === 'keyword' ? t('search.keyword') : m === 'semantic' ? t('search.semantic') : t('search.hybrid')}
              </button>
            ))}
          </div>

          {/* Account filter */}
          {accounts.length > 1 && (
            <select
              value={accountId || ''}
              onChange={(e) => handleAccountChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('account_setup.all_accounts')}</option>
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>{acc.email}</option>
              ))}
            </select>
          )}

          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {t('search.search')}
          </button>
        </form>
      </div>

      {/* Results info bar */}
      {query && !loading && (
        <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between text-sm">
          <span className="text-gray-600">
            {totalCount === 0 ? (
              t('search.no_results')
            ) : (
              <>
                {t('search.found_results', { count: totalCount, query })}
              </>
            )}
          </span>
          <span className="text-gray-500 capitalize">
            {t('search.mode')}: {mode}
          </span>
        </div>
      )}

      {/* Results list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <span className="text-gray-500 text-sm">{t('search.searching')}</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="text-red-500 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-gray-600">{error}</p>
              <button
                onClick={() => performSearch(query, mode, accountId)}
                className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                {t('search.try_again')}
              </button>
            </div>
          </div>
        ) : results.length === 0 && query ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center max-w-md px-4">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-1">{t('search.no_results')}</h3>
              <p className="text-gray-500 text-sm">
                {mode === 'keyword' ? (
                  t('search.no_results_keyword_hint')
                ) : mode === 'semantic' ? (
                  t('search.no_results_semantic_hint')
                ) : (
                  t('search.no_results_hybrid_hint')
                )}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {results.map((result) => (
              <div
                key={result.message_id}
                onClick={() => handleResultClick(result)}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Subject and match type */}
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-900 truncate group-hover:text-blue-600 transition">
                        {result.subject || t('common.no_subject')}
                      </h3>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-medium ${
                        MATCH_TYPE_STYLES[result.match_type]?.bg || 'bg-gray-100'
                      } ${MATCH_TYPE_STYLES[result.match_type]?.text || 'text-gray-600'}`}>
                        {MATCH_TYPE_STYLES[result.match_type]?.label || result.match_type}
                      </span>
                    </div>

                    {/* Sender */}
                    <p className="text-sm text-gray-600 mb-1">
                      {result.sender_name || result.sender_email}
                      {result.sender_name && result.sender_email && (
                        <span className="text-gray-400 ml-1">&lt;{result.sender_email}&gt;</span>
                      )}
                    </p>

                    {/* Snippet or chunk text */}
                    <div className="text-sm text-gray-500 line-clamp-2">
                      {result.highlight ? (
                        renderHighlight(result.highlight)
                      ) : result.chunk_text ? (
                        <span className="italic">{result.chunk_text}</span>
                      ) : (
                        result.snippet
                      )}
                    </div>

                    {/* Score (if available) */}
                    {result.score !== undefined && result.score !== null && (
                      <div className="mt-1 flex items-center gap-2">
                        <div className="h-1.5 w-20 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${Math.min(result.score * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400">
                          {(result.score * 100).toFixed(0)}% match
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Timestamp */}
                  <div className="flex-shrink-0 text-xs text-gray-400">
                    {result.timestamp ? formatEmailDate(result.timestamp) : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
